import { REQUIRED_EXTENSIONS, ZIP_EXTENSION } from './constants';
import type {
    NormalizedShapefileBuffers,
    PartialShapefileBuffers,
    ShapefileInput,
    ShapefileParseError
} from './types';

// Lowercased extension (everything after the final dot) or empty string if
// the file has no extension.
const extensionOf = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf('.');

    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return '';
    }

    return fileName.slice(dotIndex + 1).toLowerCase();
};

const decodePrj = (buffer: ArrayBuffer): string => new TextDecoder('utf-8').decode(buffer);

const isSupportedExtension = (
    extension: string
): extension is (typeof REQUIRED_EXTENSIONS)[number] =>
    (REQUIRED_EXTENSIONS as readonly string[]).includes(extension);

// Read the ArrayBuffer for each recognized file in order, short-circuiting
// on the first extension that maps to something shpjs needs. Files whose
// extensions are not in REQUIRED_EXTENSIONS are silently skipped — callers
// commonly include .cpg, .qmd, .sbn, etc. alongside the four we care about.
const collectBuffers = async ({ files }: { files: File[] }): Promise<PartialShapefileBuffers> => {
    const buffers: PartialShapefileBuffers = {};

    await Promise.all(
        files.map(async file => {
            const extension = extensionOf(file.name);

            if (!isSupportedExtension(extension)) {
                return;
            }

            const arrayBuffer = await file.arrayBuffer();

            if (extension === 'prj') {
                buffers.prj = decodePrj(arrayBuffer);

                return;
            }

            buffers[extension] = arrayBuffer;
        })
    );

    return buffers;
};

const missingFileError = (
    partial: PartialShapefileBuffers
): Extract<ShapefileParseError, { kind: 'missingFile' }> | null => {
    if (!partial.shp) {
        return { file: 'shp', kind: 'missingFile' };
    }

    if (!partial.shx) {
        return { file: 'shx', kind: 'missingFile' };
    }

    if (!partial.dbf) {
        return { file: 'dbf', kind: 'missingFile' };
    }

    if (!partial.prj) {
        return { file: 'prj', kind: 'missingFile' };
    }

    return null;
};

// Public: normalize the caller's ShapefileInput into either a ready-to-parse
// ArrayBuffer for the .zip path or a NormalizedShapefileBuffers for the
// individual-files path. Returns a typed error for the four-file case when
// any required extension is missing.
export interface ReadFilesResult {
    mode: 'zip' | 'files';
    zipBuffer?: ArrayBuffer;
    buffers?: NormalizedShapefileBuffers;
    error?: ShapefileParseError;
}

export const readShapefileInput = async (input: ShapefileInput): Promise<ReadFilesResult> => {
    if ('zip' in input) {
        const extension = extensionOf(input.zip.name);

        if (extension !== ZIP_EXTENSION) {
            return {
                error: {
                    detail: `Expected a .zip file, received .${extension}`,
                    kind: 'shpjsFailure'
                },
                mode: 'zip'
            };
        }

        const zipBuffer = await input.zip.arrayBuffer();

        return { mode: 'zip', zipBuffer };
    }

    const partial = await collectBuffers({ files: input.files });
    const missing = missingFileError(partial);

    if (missing) {
        return { error: missing, mode: 'files' };
    }

    const buffers: NormalizedShapefileBuffers = {
        dbf: partial.dbf as ArrayBuffer,
        prj: partial.prj as string,
        shp: partial.shp as ArrayBuffer,
        shx: partial.shx as ArrayBuffer
    };

    return { buffers, mode: 'files' };
};
